import { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS, REPORT_DATE_TYPES } from "../assets/constants";
import * as Sentry from "@sentry/react";
import { withSpan, addBreadcrumb, captureException, setSpanAttribute, formatRequestBody, setRequestBodyContext, clearRequestBodyContext } from "../utils/sentry";

// Using Vite environment variables
const BASE_URL_V2 = import.meta.env.VITE_API_V2_URL;
let isRefreshing = false;
let requestQueue = [];

// Shared utilities for reports
const REPORT_DATE_TYPES_MAP = {
  itemWise: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
  categoryWise: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
  orderTypeRevenue: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
  cancelledBill: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
  cancelledBillItem: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
  revenue: [
    REPORT_DATE_TYPES.CUSTOM,
    REPORT_DATE_TYPES.TODAY,
    REPORT_DATE_TYPES.YESTERDAY,
    REPORT_DATE_TYPES.LAST_7_DAYS,
    REPORT_DATE_TYPES.LAST_30_DAYS,
    REPORT_DATE_TYPES.LAST_90_DAYS,
    REPORT_DATE_TYPES.LAST_180_DAYS,
  ],
};

const validateReportDateType = (dateType, reportType) => {
  const allowedDateTypes = REPORT_DATE_TYPES_MAP[reportType];
  if (!allowedDateTypes.includes(dateType)) {
    throw new Error(
      `Invalid dateType. Allowed values for ${reportType} report are: ${allowedDateTypes.join(
        ", "
      )}`
    );
  }
};

const buildReportQueryParams = ({
  dateType,
  startDate = "",
  endDate = "",
  date = "",
  page,
  pageSize,
}) => {
  const queryParams = new URLSearchParams({ dateType });

  // Add pagination if provided
  if (page !== undefined) queryParams.append("page", page.toString());
  if (pageSize !== undefined)
    queryParams.append("pageSize", pageSize.toString());

  // Add date parameters based on type
  if (dateType === REPORT_DATE_TYPES.ONE_DAY && date) {
    queryParams.append("date", date);
  }
  if (
    (dateType === REPORT_DATE_TYPES.ONE_DAY ||
      dateType === REPORT_DATE_TYPES.CUSTOM) &&
    startDate
  ) {
    queryParams.append("startDate", startDate);
  }
  if (
    (dateType === REPORT_DATE_TYPES.ONE_DAY ||
      dateType === REPORT_DATE_TYPES.CUSTOM) &&
    endDate
  ) {
    queryParams.append("endDate", endDate);
  }

  return queryParams;
};

const handleReportError = (err, reportType) => {
  console.log(err);
  return {
    status: "failed",
    message: err.message || `Failed to fetch ${reportType} report`,
    data: null,
  };
};

export const useCloud = () => {
  const [token, setToken] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await localStorage.getItem("authToken");
      setToken(storedToken);
    };
    fetchToken();
  }, []);

  const apiInstance_V2 = axios.create({
    baseURL: BASE_URL_V2,
  });

  useEffect(() => {
    apiInstance_V2.interceptors.request.use(
      async (config) => {
        const token = await localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if(config.headers["Content-Type"] === undefined){
          config.headers["Content-Type"] = "application/json";
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    apiInstance_V2.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip if not 401
        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        if (originalRequest.url.includes("/login")) {
          return Promise.reject(error);
        }

        // Skip if it's the refresh call itself
        if (originalRequest.url.includes("/refresh")) {
          logout();
          return Promise.reject(error);
        }

        // Avoid infinite loop
        if (originalRequest._retry) {
          logout();
          return Promise.reject(error);
        }
        originalRequest._retry = true;

        // If already refreshing, queue the request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            requestQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return apiInstance_V2(originalRequest);
            })
            .catch((err) => {
              logout();
              return Promise.reject(err);
            });
        }

        // Start refresh flow
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          refreshToken()
            .then(({ data }) => {
              const { accessToken, refreshToken } = data;

              // Save tokens
              localStorage.setItem("authToken", accessToken);
              localStorage.setItem("refreshToken", refreshToken);

              // Update default header
              apiInstance_V2.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${accessToken}`;

              // Retry the original request
              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${accessToken}`;
              resolve(apiInstance_V2(originalRequest));

              // Retry any queued requests
              requestQueue.forEach((p) => p.resolve(accessToken));
              requestQueue = [];
            })
            .catch((err) => {
              // Fail all queued requests
              requestQueue.forEach((p) => p.reject(err));
              requestQueue = [];

              logout();
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
    );
  }, [token, apiInstance_V2]);

  const login = async (payload) => {
    return withSpan(
      {
        name: "API Login",
        op: "http.client",
        function: "login",
        attributes: {
          "http.method": "POST",
          "http.url": API_ENDPOINTS.LOGIN_V2,
          "http.request.body": formatRequestBody(payload),
        },
      },
      async (span) => {
        try {
          // Set request body in context (like restaurant info)
          setRequestBodyContext(API_ENDPOINTS.LOGIN_V2, payload);
          
          addBreadcrumb("Attempting login", "api.login");
          const res = await apiInstance_V2.post(API_ENDPOINTS.LOGIN_V2, payload);
          setSpanAttribute(span, "http.status_code", res.status);
          setSpanAttribute(span, "login.success", true);
          span?.setStatus("ok");
          
          // Clear context after successful request
          clearRequestBodyContext();
          return { status: "success", data: res.data };
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          setSpanAttribute(span, "login.success", false);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "login" },
            extra: { endpoint: API_ENDPOINTS.LOGIN_V2 },
          });
          
          // Clear context on error
          clearRequestBodyContext();
          return err.response?.data || { status: "error", data: null };
        }
      }
    );
  };

  const refreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    return apiInstance_V2.post(API_ENDPOINTS.REFRESH_TOKEN, { refreshToken });
  };

  const logout = () => {
    if (window.location.pathname === "/login") return;

    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/login"; // or any other logout logic
  };

  const updateGST = async (payload) => {
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.GST, payload, "PATCH");
      
      const res = await apiInstance_V2.patch(API_ENDPOINTS.GST, payload);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const dashboard = async ({
    startDate = "",
    endDate = "",
    dateType = "all",
  }) => {
    return withSpan(
      {
        name: "API Get Dashboard",
        op: "http.client",
        function: "dashboard",
        attributes: {
          "http.method": "GET",
          "http.url": API_ENDPOINTS.DASHBOARD_V2,
          "dashboard.date_type": dateType,
        },
      },
      async (span) => {
        try {
          addBreadcrumb("Fetching dashboard data", "api.dashboard", { dateType });
          const res = await apiInstance_V2.get(
            `${API_ENDPOINTS.DASHBOARD_V2}?startDate=${startDate}&endDate=${endDate}&dateType=${dateType}`
          );
          setSpanAttribute(span, "http.status_code", res.status);
          span?.setStatus("ok");
          return res.data;
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "dashboard" },
            extra: { endpoint: API_ENDPOINTS.DASHBOARD_V2, dateType },
          });
          return err.response?.data || { status: "failed", data: null };
        }
      }
    );
  };

  const getRestaurantInfo = async () => {
    return withSpan(
      {
        name: "API Get Restaurant Info",
        op: "http.client",
        function: "getRestaurantInfo",
        attributes: {
          "http.method": "GET",
          "http.url": API_ENDPOINTS.RESTAURANT_DETAILS,
        },
      },
      async (span) => {
        try {
          addBreadcrumb("Fetching restaurant info", "api.restaurant");
          const res = await apiInstance_V2.get(API_ENDPOINTS.RESTAURANT_DETAILS);
          setSpanAttribute(span, "http.status_code", res.status);
          span?.setStatus("ok");
          
          // Update user context after fetching restaurant info
          if (res.data?.data) {
            const { updateUserContext } = await import("../utils/sentryInit");
            updateUserContext();
          }
          
          return res.data;
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "getRestaurantInfo" },
            extra: { endpoint: API_ENDPOINTS.RESTAURANT_DETAILS },
          });
          return err.response?.data || { status: "failed", data: null };
        }
      }
    );
  };

  const getMenu = async () => {
    return withSpan(
      {
        name: "API Get Menu",
        op: "http.client",
        function: "getMenu",
        attributes: {
          "http.method": "GET",
          "http.url": API_ENDPOINTS.MENU,
        },
      },
      async (span) => {
        try {
          addBreadcrumb("Fetching menu", "api.menu");
          const res = await apiInstance_V2.get(API_ENDPOINTS.MENU);
          setSpanAttribute(span, "http.status_code", res.status);
          span?.setStatus("ok");
          return res.data;
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "getMenu" },
            extra: { endpoint: API_ENDPOINTS.MENU },
          });
          return err.response?.data || { status: "failed", data: null };
        }
      }
    );
  };

  const createOrUpdateCategory = async (category) => {
    try {
      let url = API_ENDPOINTS.CATEGORY;
      if (category.categoryId != undefined) {
        url += `/${category.categoryId}`;
      }
      delete category.categoryImage;
      const res = await apiInstance_V2[
        category.categoryId === undefined ? "post" : "put"
      ](url, category);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      let url = API_ENDPOINTS.CATEGORY + `/${categoryId}`;
      const res = await apiInstance_V2["delete"](url);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const createOrUpdateMenuItem = async (menuItem) => {
    try {
      delete menuItem["categoryDetails"];
      delete menuItem["images"];
      let url = API_ENDPOINTS.MENU;
      if (menuItem.id != undefined) {
        url += `/${menuItem.id}`;
      }
      const res = await apiInstance_V2[menuItem.id === "" ? "post" : "put"](
        url,
        menuItem
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const deleteMenuItem = async (id) => {
    try {
      const url = `${API_ENDPOINTS.MENU}/${id}`;
      const res = await apiInstance_V2["delete"](url);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const reports = async ({
    customStart = "",
    customEnd = "",
    dateType = "today",
    pageSize = 20,
    page = 1,
    billStatus = "all",
  }) => {
    try {
      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.REPORTS_V2}?startDate=${customStart}&endDate=${customEnd}&dateType=${dateType}&pageSize=${pageSize}&page=${page}&billStatus=${billStatus}`
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const liveOrders = async () => {
    // Excluded from Sentry tracking - high frequency polling endpoint
    try {
      const res = await apiInstance_V2.get(API_ENDPOINTS.ORDER_LIVE_V2);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const createOrder = async (order) => {
    return withSpan(
      {
        name: "API Create Order",
        op: "http.client",
        function: "createOrder",
        attributes: {
          "http.method": "POST",
          "http.url": API_ENDPOINTS.ORDER,
          "order.table_vpa": order?.tableVPA || "unknown",
          "order.items_count": order?.items?.length || 0,
          "http.request.body": formatRequestBody(order),
        },
      },
      async (span) => {
        try {
          // Set request body in context (like restaurant info)
          setRequestBodyContext(API_ENDPOINTS.ORDER, order);
          
          addBreadcrumb("Creating order", "api.order.create", {
            tableVPA: order?.tableVPA,
            itemsCount: order?.items?.length || 0,
          });
          const res = await apiInstance_V2.post(API_ENDPOINTS.ORDER, order);
          setSpanAttribute(span, "http.status_code", res.status);
          setSpanAttribute(span, "order.id", res.data?.data?.orderId || "unknown");
          span?.setStatus("ok");
          
          // Clear context after successful request
          clearRequestBodyContext();
          return res.data;
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "createOrder" },
            extra: { endpoint: API_ENDPOINTS.ORDER, tableVPA: order?.tableVPA },
          });
          
          // Clear context on error
          clearRequestBodyContext();
          return err.response?.data || { status: "failed", data: null };
        }
      }
    );
  };

  const updateOrder = async (update) => {
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.ORDER, update, "PATCH");
      
      const res = await apiInstance_V2.patch(API_ENDPOINTS.ORDER, update);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const settleOrder = async (order) => {
    return withSpan(
      {
        name: "API Settle Order",
        op: "http.client",
        function: "settleOrder",
        attributes: {
          "http.method": "POST",
          "http.url": API_ENDPOINTS.ORDER_PAYMENT,
          "order.id": order?.orderId || "unknown",
          "order.table_vpa": order?.tableVPA || "unknown",
          "http.request.body": formatRequestBody(order),
        },
      },
      async (span) => {
        try {
          // Set request body in context (like restaurant info)
          setRequestBodyContext(API_ENDPOINTS.ORDER_PAYMENT, order);
          
          addBreadcrumb("Settling order payment", "api.order.settle", {
            orderId: order?.orderId,
            tableVPA: order?.tableVPA,
          });
          const res = await apiInstance_V2.post(API_ENDPOINTS.ORDER_PAYMENT, order);
          setSpanAttribute(span, "http.status_code", res.status);
          span?.setStatus("ok");
          
          // Clear context after successful request
          clearRequestBodyContext();
          return res.data;
        } catch (err) {
          console.log(err);
          setSpanAttribute(span, "http.status_code", err.response?.status || 0);
          span?.setStatus("internal_error");
          captureException(err, {
            tags: { component: "useCloud", action: "settleOrder" },
            extra: { endpoint: API_ENDPOINTS.ORDER_PAYMENT, orderId: order?.orderId },
          });
          
          // Clear context on error
          clearRequestBodyContext();
          return err.response?.data || { status: "failed", data: null };
        }
      }
    );
  };

  const getOrderDetails = async (payload) => {
    try {
      const res = await apiInstance_V2.get(API_ENDPOINTS.ORDER_BY_ID(payload));
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const outOfStock = async (requestBody) => {
    try {
      const { itemId, categoryId, status } = requestBody;
      const url = `${API_ENDPOINTS.MENU}/${itemId}/status`;
      const res = await apiInstance_V2.put(url, {
        categoryId,
        status,
      });
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const updateItemsRank = async (requestBody) => {
    try {
      const url = `${API_ENDPOINTS.MENU}/update-item-ranks`;
      const res = await apiInstance_V2.put(url, requestBody);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const updateCategoryRank = async (requestBody) => {
    try {
      const url = `${API_ENDPOINTS.CATEGORY}/update-category-ranks`;
      
      // Set request body in context (like restaurant info)
      setRequestBodyContext(url, requestBody, "PUT");
      
      const res = await apiInstance_V2.put(url, requestBody);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const quickOrder = async (payload) => {
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.QUICK_ORDER, payload);
      
      const res = await apiInstance_V2.post(API_ENDPOINTS.QUICK_ORDER, payload);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const updatePartners = async (payload) => {
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.PARTNERS, payload);
      
      const res = await apiInstance_V2.post(API_ENDPOINTS.PARTNERS, payload);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };
  const getPrinterConfig = async () => {
    try {
      const res = await apiInstance_V2.get(API_ENDPOINTS.PRINTER_CONFIG);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const savePrinterConfig = async (requestBody) => {
    try {
      const res = await apiInstance_V2.patch(
        API_ENDPOINTS.PRINTER_CONFIG,
        requestBody
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  // REPORTS
  const itemWiseReport = async ({
    dateType = REPORT_DATE_TYPES.TODAY,
    startDate = "",
    endDate = "",
    page = 1,
    pageSize = 20,
  }) => {
    try {
      validateReportDateType(dateType, "itemWise");

      if (dateType === REPORT_DATE_TYPES.CUSTOM && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required for custom dateType"
        );
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
        page,
        pageSize,
      });

      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.ITEM_WISE_REPORTS_V2}?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "item-wise");
    }
  };

  const categoryWiseReport = async ({
    dateType = REPORT_DATE_TYPES.TODAY,
    date = "",
    page = 1,
    pageSize = 20,
    startDate = "",
    endDate = "",
  }) => {
    try {
      validateReportDateType(dateType, "categoryWise");

      if (dateType === REPORT_DATE_TYPES.ONE_DAY && !date) {
        throw new Error("date parameter is required when dateType is '1day'");
      }

      const queryParams = buildReportQueryParams({
        dateType,
        date,
        page,
        pageSize,
        startDate,
        endDate,
      });

      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.CATEGORY_WISE_REPORTS_V2}?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "category-wise");
    }
  };

  const orderTypeRevenueReport = async ({
    dateType = REPORT_DATE_TYPES.TODAY,
    startDate = "",
    endDate = "",
  }) => {
    try {
      validateReportDateType(dateType, "orderTypeRevenue");

      if (dateType === REPORT_DATE_TYPES.ONE_DAY && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required when dateType is '1day'"
        );
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
      });

      const res = await apiInstance_V2.get(
        `${
          API_ENDPOINTS.ORDER_TYPE_REVENUE_REPORTS_V2
        }?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "order-type-revenue");
    }
  };

  const cancelledBillReport = async ({
    dateType = REPORT_DATE_TYPES.TODAY,
    startDate = "",
    endDate = "",
    page = 1,
    pageSize = 20,
  }) => {
    try {
      validateReportDateType(dateType, "cancelledBill");

      if (dateType === REPORT_DATE_TYPES.ONE_DAY && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required when dateType is '1day'"
        );
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
        page,
        pageSize,
      });

      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.CANCELLED_BILL_REPORTS_V2}?${queryParams.toString()}`
      );
      return res;
    } catch (err) {
      return handleReportError(err, "cancelled-bill");
    }
  };

  const cancelledBillItemReport = async ({
    dateType = REPORT_DATE_TYPES.TODAY,
    startDate = "",
    endDate = "",
    page = 1,
    pageSize = 20,
  }) => {
    try {
      validateReportDateType(dateType, "cancelledBillItem");

      if (dateType === REPORT_DATE_TYPES.ONE_DAY && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required when dateType is '1day'"
        );
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
        page,
        pageSize,
      });

      const res = await apiInstance_V2.get(
        `${
          API_ENDPOINTS.CANCELLED_BILL_ITEM_REPORTS_V2
        }?${queryParams.toString()}`
      );
      return res;
    } catch (err) {
      return handleReportError(err, "cancelled-bill-item");
    }
  };

  const revenueReport = async ({
    dateType = "",
    startDate = "",
    endDate = "",
    date = "",
    page = 1,
    pageSize = 20,
  }) => {
    try {
      // For revenue report, startDate and endDate are required
      if (dateType === REPORT_DATE_TYPES.CUSTOM && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required for revenue report"
        );
      }

      // If dateType is provided, validate it
      if (dateType) {
        validateReportDateType(dateType, "revenue");
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
        date,
        page,
        pageSize,
      });

      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.REVENUE_REPORTS_V2}?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "revenue");
    }
  };

  const dateWiseItemReport = async ({
    dateType = "",
    startDate = "",
    endDate = "",
    date = "",
    page = 1,
    pageSize = 20,
  }) => {
    try {
      // For revenue report, startDate and endDate are required
      if (dateType === REPORT_DATE_TYPES.CUSTOM && (!startDate || !endDate)) {
        throw new Error(
          "startDate and endDate are required for revenue report"
        );
      }

      // If dateType is provided, validate it
      if (dateType) {
        validateReportDateType(dateType, "revenue");
      }

      const queryParams = buildReportQueryParams({
        dateType,
        startDate,
        endDate,
        date,
        page,
        pageSize,
      });

      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.DATE_WISE_ITEM_REPORT}?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "date-wise-item");
    }
  };

  const partnerOrderReport = async ({
    dateType = "",
    startDate = "",
    endDate = "",
    date = "",
  }) => {
    const queryParams = buildReportQueryParams({
      dateType,
      startDate,
      endDate,
      date,
    });

    try {
      const res = await apiInstance_V2.get(
        `${API_ENDPOINTS.PARTNER_ORDER_REPORT}?${queryParams.toString()}`
      );
      return res.data;
    } catch (err) {
      return handleReportError(err, "partner-order");
    }
  };

  const verifyCoupon = async ({ couponCode, customerDetail, billId, billAmount }) => {
    const requestBody = {
      couponCode,
      customerDetail,
      billId,
      billAmount
    };
    
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.VERIFY_COUPON, requestBody);
      
      const res = await apiInstance_V2.post(API_ENDPOINTS.VERIFY_COUPON, requestBody);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  // STAFF
  const addNewStaff = async (payload) => {
    try {
      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.STAFF, payload);
      
      const res = await apiInstance_V2.post(API_ENDPOINTS.STAFF, payload);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const updateStaff = async (payload) => {
    try {
      const { staffId, ...rest } = payload;
      const url = `${API_ENDPOINTS.STAFF}/${staffId}`;
      
      // Set request body in context (like restaurant info)
      setRequestBodyContext(url, rest, "PUT");
      
      const res = await apiInstance_V2.put(url, rest);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const removeStaff = async (staffId) => {
    try {
      const url = `${API_ENDPOINTS.STAFF}/${staffId}`;
      const res = await apiInstance_V2.delete(url);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const uploadMenuItemImage = async (categoryId, menuId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('categoryId', categoryId.toString());
      formData.append('menuId', menuId.toString());
      formData.append('image', imageFile);

      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.MENU_IMAGE, formData);

      const res = await apiInstance_V2.post(API_ENDPOINTS.MENU_IMAGE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const deleteMenuItemImage = async (categoryId, menuId) => {
    try {
      const res = await apiInstance_V2.delete(API_ENDPOINTS.MENU_IMAGE, {
        data: {
          categoryId,
          menuId
        }
      });
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const uploadCategoryImage = async (categoryId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('categoryId', categoryId.toString());
      formData.append('image', imageFile);

      // Set request body in context (like restaurant info)
      setRequestBodyContext(API_ENDPOINTS.CATEGORY_IMAGE, formData);

      const res = await apiInstance_V2.post(API_ENDPOINTS.CATEGORY_IMAGE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };


  const downloadReport = async (payload) => {
    try {
      const res = await apiInstance_V2.get(
        API_ENDPOINTS.DOWNLOAD_REPORT,
        payload
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const createDownloadRequest = async (payload) => {
    try {
      let url = `${API_ENDPOINTS.EXPORT_REPORT}?reportType=${
        payload.reportType
      }&${buildReportQueryParams(payload).toString()}`;

      // Set request body in context (like restaurant info)
      // Note: This is a POST with query params, but we'll include payload in context
      setRequestBodyContext(url, payload);

      const res = await apiInstance_V2.post(url);
      
      // Clear context after successful request
      clearRequestBodyContext();
      return res.data;
    } catch (err) {
      console.log(err);
      
      // Clear context on error
      clearRequestBodyContext();
      return err.response?.data || { status: "failed", data: null };
    }
  };

  const generateEbillPdf = async (orderId, customerName, phoneNumber) => {
    try {
      const params = new URLSearchParams();
      if (customerName) {
        params.append('customerName', customerName);
      }
      if (phoneNumber) {
        params.append('phoneNumber', phoneNumber);
      }
      const queryString = params.toString();
      const url = queryString 
        ? `${API_ENDPOINTS.EBILL_PDF(orderId)}?${queryString}`
        : API_ENDPOINTS.EBILL_PDF(orderId);
      const res = await apiInstance_V2.get(url);
      return res.data;
    } catch (err) {
      console.log(err);
      return err.response?.data || { status: "failed", data: null };
    }
  };

  return {
    // V2
    getRestaurantInfo,
    outOfStock,
    login,
    updateGST,
    dashboard,
    reports,
    itemWiseReport,
    categoryWiseReport,
    orderTypeRevenueReport,
    cancelledBillReport,
    cancelledBillItemReport,
    revenueReport,
    liveOrders,
    createOrder,
    updateOrder,
    settleOrder,
    getMenu,
    createOrUpdateCategory,
    deleteCategory,
    createOrUpdateMenuItem,
    deleteMenuItem,
    getOrderDetails,
    updateItemsRank,
    updateCategoryRank,
    quickOrder,
    updatePartners,
    getPrinterConfig,
    savePrinterConfig,
    dateWiseItemReport,
    addNewStaff,
    updateStaff,
    removeStaff,
    uploadMenuItemImage,
    deleteMenuItemImage,
    uploadCategoryImage,
    downloadReport,
    createDownloadRequest,
    partnerOrderReport,
    verifyCoupon,
    generateEbillPdf,
  };
};
